<template>
	<div class="star-rating flex justify-center">
		<button
			v-for="star in 5"
			:key="star"
			type="button"
			class="material-icons-outlined star-button pl-1 text-4xl"
			:class="{
				'icon-checked': star <= (hoverRating || currentRating),
				'icon-unchecked': star > (hoverRating || currentRating),
			}"
			@click="selectRating(star)"
			@mouseenter="hoverRating = star"
			@mouseleave="hoverRating = 0"
		></button>
	</div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";

interface Props {
	rating?: number;
	id: string | number;
	readonly?: boolean;
}

interface Emits {
	(e: "update:rating", rating: number, id: string | number): void;
	(e: "ratingSelected", rating: number, id: string | number): void;
}

const props = withDefaults(defineProps<Props>(), {
	rating: 0,
	readonly: false,
});

const emit = defineEmits<Emits>();

const currentRating = ref(props.rating);
const hoverRating = ref(0);

// Watch for external rating changes
watch(
	() => props.rating,
	(newRating) => {
		currentRating.value = newRating;
	}
);

function selectRating(rating: number) {
	if (props.readonly) return;

	currentRating.value = rating;
	emit("update:rating", rating, props.id);
	emit("ratingSelected", rating, props.id);
}
</script>

<style scoped>
.star-button {
	cursor: pointer;
	user-select: none;
}

.star-button:disabled {
	cursor: not-allowed;
	opacity: 0.6;
}

.star-rating.readonly .star-button {
	cursor: default;
}

.star-rating.readonly .star-button:hover {
	transform: none;
}

.icon-checked {
	@apply after:content-['favorite'] text-white before:block before:absolute before:w-dj-40 before:h-dj-40;
}

.icon-unchecked {
	@apply after:content-['favorite\_border'] text-white hover:text-white;
}
</style>
